import {SimpleGridProps} from "@mantine/core";
import {
  StatisticsBundleWrapper,
  toTransportRejectionType,
  TransportCategory,
  transportRejectionTypes
} from "@ots-frontend/ots-data";
import {InsightEmpty} from "../InsightEmpty/InsightEmpty";
import {useMemo} from "react";
import {BarChart, BarChartBucketData, BarChartData, BarChartProps} from "../../chart/BarChart/BarChart";
import {InsightChartContainer, InsightChartContainerProps} from "../InsightChartContainer/InsightChartContainer";
import {useTranslation} from "react-i18next";

export type RejectsByCompanyProps =
  Pick<InsightChartContainerProps, 'containerProps' | 'loading'>
  & Pick<BarChartProps, 'id'>
  & SimpleGridProps
  & {
  statistics?: StatisticsBundleWrapper
  category?: TransportCategory
}

export const RejectsByCompanyInsights = (props: RejectsByCompanyProps) => {
  const {
    id,
    statistics,
    category,
    ...rest
  } = props

  const {t} = useTranslation()

  const chartData: BarChartData | undefined = useMemo(() => {
    if (statistics) {
      const companies: string[] = []
      const workingBucket: Record<string, (number | [number, number] | null)[]> = {}

      transportRejectionTypes.forEach((type) => {
        workingBucket[type.code] = []
      })

      statistics.truckCompanyStats.forEach((stats) => {
        const rejectsByType = stats.trucking.getRejectsByType(category)
        if (Object.keys(rejectsByType).length > 0) {
          companies.push(stats.name)
          transportRejectionTypes.forEach((type) => {
            workingBucket[type.code].push(rejectsByType[type.code])
          })
        }
      })

      const buckets: Record<string, BarChartBucketData> = {}

      Object.keys(workingBucket).forEach((key) => {
        const numbers = workingBucket[key]
        if (numbers.filter((item) => item).length > 0) {
          const type = toTransportRejectionType(key)
          buckets[t(type.displayValue.id, type.displayValue.defaultMessage)] = {
            stacked: {
              enabled: true,
            },
            values: numbers
          }
        }
      })

      if (companies.length > 0 && Object.keys(buckets).length > 0) {
        return {
          titles: companies,
          buckets: buckets,
        }
      }

      return undefined
    }

    return undefined
  }, [statistics, category, t])

  const title = useMemo(() => {
    return t("common.insight.rejectsbycompany", "Rejects By Company")
  }, [t])

  if (!chartData) {
    return <InsightEmpty title={title} />
  }

  return (
    <InsightChartContainer {...rest} title={title}>
      <BarChart
        id={id}
        stacked={{
          enabled: true,
        }}
        xAxisTitle={t<string>("common.insight.rejectsbycompany.xaxis.title", "Company")}
        yAxisTitle={t<string>("common.insight.rejectsbycompany.yaxis.title", "# of Rejects")}
        data={chartData}/>
    </InsightChartContainer>
  )
}
